import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectGuaranteesConclusionTemplateDocument,
  selectGuaranteesConclusionTemplateDocumentStatus,
} from '~/store/guarantees/selectors.ts';
import { downloadGuaranteesClientDocument } from '~/store/guarantees/thunks/applicationThunks.ts';
import type { TGuaranteesApplicationFile } from '~/store/guarantees/types.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useGuaranteesPdfPreview } from '../../shared/hooks/useGuaranteesPdfPreview.ts';
import { UDBTF_APPROVAL_LETTER_MISSING_PDF_TEXT } from './constants.ts';
import { getApprovalLetterPreviewFileId } from './utils.ts';

type TPreviewDocument = {
  data: Blob;
  contentType?: string;
};

type TSetModalAlert = (value: { text: string }) => void;

export const useInternalApprovalConclusionPdfPreview = ({
  conclusionAttachmentFile,
  hasApplicationFilesLoaded,
  isApplicationFilesLoading,
  setModalAlert,
  showGuaranteesRequestError,
}: {
  conclusionAttachmentFile?: TGuaranteesApplicationFile | null;
  hasApplicationFilesLoaded: boolean;
  isApplicationFilesLoading: boolean;
  setModalAlert: TSetModalAlert;
  showGuaranteesRequestError: (error: unknown) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const conclusionTemplateDocument = useSelector(selectGuaranteesConclusionTemplateDocument);
  const conclusionTemplateDocumentStatus = useSelector(
    selectGuaranteesConclusionTemplateDocumentStatus
  );
  const [previewDocument, setPreviewDocument] = useState<TPreviewDocument | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const attachmentFileId = getApprovalLetterPreviewFileId(conclusionAttachmentFile);
  const documentBlob = previewDocument?.data ?? conclusionTemplateDocument?.data;
  const contentType = previewDocument?.contentType ?? conclusionTemplateDocument?.contentType;
  const pdfPreview = useGuaranteesPdfPreview({
    contentType,
    documentBlob,
    missingPdfText: UDBTF_APPROVAL_LETTER_MISSING_PDF_TEXT,
    setModalAlert,
    status: attachmentFileId ? undefined : conclusionTemplateDocumentStatus,
  });
  const isDocumentLoading =
    isApplicationFilesLoading || isPreviewLoading || pdfPreview.isDocumentLoading;

  useEffect(() => {
    if (isApplicationFilesLoading || !hasApplicationFilesLoaded) {
      return;
    }

    if (!attachmentFileId) {
      setPreviewDocument(null);

      return;
    }

    let isCancelled = false;

    const loadPreview = async () => {
      setPreviewDocument(null);
      setIsPreviewLoading(true);

      try {
        const downloadedDocument = await dispatch(
          downloadGuaranteesClientDocument({
            id: attachmentFileId,
            isAttached: false,
          })
        ).unwrap();

        if (isCancelled) {
          return;
        }

        setPreviewDocument({
          data: downloadedDocument.data,
          contentType: downloadedDocument.contentType,
        });
      } catch (error) {
        if (!isCancelled) {
          setPreviewDocument(null);
          showGuaranteesRequestError(error);
        }
      } finally {
        if (!isCancelled) {
          setIsPreviewLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      isCancelled = true;
    };
  }, [
    attachmentFileId,
    dispatch,
    hasApplicationFilesLoaded,
    isApplicationFilesLoading,
    showGuaranteesRequestError,
  ]);

  return {
    handleOpenPdf: pdfPreview.handleOpenPdf,
    isDocumentLoading,
    pdfUrl: pdfPreview.pdfUrl,
  };
};
